/**
 * Login Page - Magic link authentication
 * Design matches the prototype at resolve-ai-product-design/resolve-ai-ui
 */

import { useState, useEffect } from 'react';
import type { FormEvent } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { Mail, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { ApiError } from '../lib/api';
import { API_BASE } from '../lib/constants';
import { LoginState } from '../types/auth-states';

export function LoginPage() {
  const { sendMagicLink } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [state, setState] = useState<LoginState>(LoginState.Idle);
  const [error, setError] = useState<string | null>(null);

  // Handle OAuth error from query params (e.g., /login?error=auth_failed)
  useEffect(() => {
    const errorParam = searchParams.get('error');
    if (errorParam) {
      // Map error codes to user-friendly messages
      const errorMessages: Record<string, string> = {
        'auth_failed': 'Google authentication failed. Please try again.',
        'invalid_state': 'Session expired. Please try again.',
        'no_code': 'Authentication was cancelled.',
        'access_denied': 'Access was denied. Please try again.',
        'rate_limit': 'Too many login attempts. Please wait a minute and try again.',
      };
      setError(errorMessages[errorParam] || `Authentication error: ${errorParam}`);
      setState(LoginState.Error);

      // Clear error from URL to prevent showing on refresh
      window.history.replaceState(null, '', '/login');
    }
  }, [searchParams]);

  const handleGoogleLogin = async () => {
    // Get the original path user wanted to visit (from ProtectedRoute redirect)
    const intendedPath = (location.state as { from?: Location })?.from?.pathname || '/dashboard';
    const returnUrl = `${window.location.origin}${intendedPath}`;
    const loginUrl = `${API_BASE}/auth/google/login?return_url=${encodeURIComponent(returnUrl)}`;

    setState(LoginState.Loading);
    setError(null);

    try {
      // Use fetch with redirect: 'manual' to catch errors before navigating
      const response = await fetch(loginUrl, {
        method: 'GET',
        redirect: 'manual',
        credentials: 'include',
      });

      // If redirect (opaqueredirect), proceed with navigation
      if (response.type === 'opaqueredirect') {
        window.location.href = loginUrl;
        return;
      }

      // Handle rate limit error
      if (response.status === 429) {
        setState(LoginState.Error);
        setError('Too many login attempts. Please wait a minute and try again.');
        return;
      }

      // Handle other errors
      const data = await response.json().catch(() => ({}));
      setState(LoginState.Error);
      setError(data.message || 'Something went wrong. Please try again.');
    } catch {
      setState(LoginState.Error);
      setError('Network error. Please check your connection and try again.');
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      setError('Please enter your email');
      setState(LoginState.Error);
      return;
    }

    setState(LoginState.Loading);
    setError(null);

    try {
      await sendMagicLink(email);
      setState(LoginState.Sent);
    } catch (err) {
      setState(LoginState.Error);
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Something went wrong. Please try again.');
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col items-center justify-center p-4">
      {/* Theme Toggle */}
      <button
        onClick={toggleTheme}
        className="absolute top-4 right-4 p-2 rounded-xl bg-white dark:bg-gray-800 shadow-md hover:shadow-lg transition-all hover:scale-110 active:scale-95"
        aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
        title={`Currently ${theme} mode - Click to switch`}
      >
        {theme === 'light' ? (
          <svg className="w-6 h-6 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
          </svg>
        ) : (
          <svg className="w-6 h-6 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
        )}
      </button>

      {/* Sign In Card */}
      <div className="w-full max-w-md">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-8">
          {/* Logo and Title */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-indigo-600 rounded-xl mb-4">
              <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              Resolve AI
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              AI-Powered Bug Resolution Platform
            </p>
          </div>

          {state === LoginState.Sent ? (
            /* Success State */
            <div className="text-center py-4">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Check your email</h2>
              <p className="text-gray-600 dark:text-gray-400">
                We sent a magic link to <strong className="text-gray-900 dark:text-white">{email}</strong>
              </p>
              <p className="text-gray-500 dark:text-gray-500 text-sm mt-4">
                Click the link in the email to sign in. The link expires in 15 minutes.
              </p>
              <button
                onClick={() => {
                  setState(LoginState.Idle);
                  setEmail('');
                }}
                className="mt-6 text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 text-sm font-medium transition-colors"
              >
                Use a different email
              </button>
            </div>
          ) : (
            <>
              {/* Welcome Message */}
              <div className="mb-6">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  Welcome back
                </h2>
                <p className="text-gray-600 dark:text-gray-400 text-sm">
                  Sign in to continue to your bug tracking dashboard
                </p>
              </div>

              {/* Email Form */}
              <form
                onSubmit={handleSubmit}
                className="space-y-4 mb-6"
                aria-label="Sign in with magic link"
              >
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Email address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (error) {
                          setError(null);
                          setState(LoginState.Idle);
                        }
                      }}
                      placeholder="you@company.com"
                      className="w-full pl-10 pr-4 py-3 bg-white dark:bg-gray-700 border-2 border-gray-300 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all disabled:opacity-50"
                      disabled={state === LoginState.Loading}
                      autoComplete="email"
                      autoFocus
                    />
                  </div>
                </div>

                {/* Error Message */}
                {state === LoginState.Error && error && (
                  <div className="flex items-center gap-2 text-red-600 dark:text-red-400 text-sm">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={state === LoginState.Loading}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-xl transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {state === LoginState.Loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Mail className="w-5 h-5" />
                      Send magic link
                    </>
                  )}
                </button>

                {/* Helper Text */}
                <p className="text-center text-sm text-gray-500 dark:text-gray-400">
                  No password required. We&apos;ll send you a secure sign-in link.
                </p>
              </form>

              {/* Divider */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                    or continue with
                  </span>
                </div>
              </div>

              {/* Google Sign In Button */}
              <button
                type="button"
                onClick={handleGoogleLogin}
                disabled={state === LoginState.Loading}
                className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-white dark:bg-gray-700 border-2 border-gray-300 dark:border-gray-600 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-600 transition-all shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <span className="text-gray-700 dark:text-gray-200 font-medium">
                  Sign in with Google
                </span>
              </button>
            </>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-gray-600 dark:text-gray-400 mt-8">
          &copy; {new Date().getFullYear()} Resolve AI. All rights reserved.
        </p>
      </div>
    </div>
  );
}
